import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import clsx from 'clsx';

import API from '@/apiRequest';
import ClientFiltersBar from '@/components/ClientFiltersBar/ClientFiltersBar';
import FeatureSwitch from '@/components/common/FeatureSwitch';
import ExtraActions from '@/components/ExtraActions';
import ChecklistForm from '@/components/ExtraActions/ChecklistForm';
import RepairBudgetForm from '@/components/ExtraActions/RepairBudgetForm';
import OrderToolbar from '@/components/OrderToolbar/OrderToolbar';
import Empty from '@/Empty';
import Loading from '@/Loading';
import OrderDetails from '@/orderDetails/OrderDetails';
import EditOrderFormPage from '@/orderForm/OrderForm/OrderFormPage/EditOrderFormPage';
import OrdersList from '@/OrdersList';
import { useOrderView } from '@/contexts/OrderViewContext';
import { useOrders } from '@/hooks/useOrders';
import { clearCurrentOrder } from '@/store/ducks/orders';
import { useOrderMutations } from '@/hooks/useOrderMutations';
import { setQuery } from '@/store/ducks/filter';

const ClientOrders = ({ component }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const { activeOrderId, setActiveOrderId, setActiveOrder, selectedOrders, setSelectedOrders, clearSelection } =
    useOrderView();

  const [searchString, setSearchString] = useState('');

  useEffect(() => {
    dispatch(setQuery({ text: searchString || '' }));
  }, [searchString, dispatch]);
  const [searchTags, setSearchTags] = useState([]);
  const [searchType, setSearchType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrderByDir, setFilterOrderByDir] = useState('desc');

  const prevPathRef = useRef(location.pathname);

  const { data, fetchNextPage, hasNextPage, isLoading, isRefetching, refetch } = useOrders({
    search: searchString,
    tags: searchTags,
    type: searchType,
    status: filterStatus,
    orderByDir: filterOrderByDir,
  });

  const orders = useMemo(() => {
    return data?.pages?.flatMap(page => page) || [];
  }, [data]);

  const { resendEnvelope } = useOrderMutations(activeOrderId || params?.orderId);

  useEffect(() => {
    return () => {
      dispatch(clearCurrentOrder());
      dispatch(setQuery({}));
      clearSelection();
    };
  }, [dispatch, clearSelection]);

  useEffect(() => {
    if (location.pathname === '/orders' && prevPathRef.current !== '/orders') {
      setActiveOrderId(null);
      clearSelection();
      setSelectedItem(null);
      dispatch(clearCurrentOrder());
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, setActiveOrderId, clearSelection, dispatch]);

  const handleStatusChange = useCallback((status, defaultDir) => {
    setFilterStatus(status);
    setFilterOrderByDir(defaultDir);
    setSearchString('');
    setSearchTags([]);
    setSearchType('');
  }, []);

  const handleOrderByDirChange = useCallback(dir => {
    setFilterOrderByDir(dir);
  }, []);

  const orderShowDetailsAction = useCallback(
    orderIndex => {
      const order = orders[orderIndex];
      if (!order) return;
      setActiveOrderId(order.id);
      setActiveOrder(order);
      clearSelection();
      setSelectedItem(order);
      navigate(`/orders/${order.id}`);
    },
    [orders, navigate, setActiveOrderId, setActiveOrder, clearSelection]
  );

  const orderShowEditAction = useCallback(() => {
    let targetId = activeOrderId || params?.orderId;
    if (selectedOrders.size === 1) {
      targetId = Array.from(selectedOrders)[0];
    }
    if (!targetId) return;

    clearSelection();
    setSelectedItem(null);
    setActiveOrderId(targetId);
    navigate(`/orders/${targetId}/edit`);
  }, [activeOrderId, params?.orderId, selectedOrders, clearSelection, setActiveOrderId, navigate]);

  const orderDeleteAction = useCallback(async () => {
    const id = activeOrderId || params?.orderId;
    if (!id) return;

    const message = intl.formatMessage({ id: 'MESSAGE.CANCEL_CONFIRMATION' });
    if (!window.confirm(message)) return;

    try {
      await API.delete(`orders/${id}`);
      setActiveOrderId(null);
      clearSelection();
      setSelectedItem(null);

      if (location.pathname !== '/orders') {
        navigate('/orders');
      }
      refetch();
    } catch (err) {
      console.error('[ClientOrders] Delete failed', err);
    }
  }, [activeOrderId, params?.orderId, intl, navigate, location.pathname, clearSelection, setActiveOrderId, refetch]);

  const actionGeneratePhotos = useCallback(async () => {
    const id = activeOrderId || params?.orderId;
    if (!id) return;
    try {
      await API.post(`orders/${id}/photos`, {});
    } catch (error) {
      console.error('[ClientOrders] Generate photos failed', error);
    }
  }, [activeOrderId, params?.orderId]);

  const checkOneItemAction = useCallback(
    (id, e) => {
      e.stopPropagation();
      const newSelected = new Set(selectedOrders);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.add(id);
      }

      let newOrderId = null;
      let newSelectedItem = null;
      if (newSelected.size === 1) {
        const item = orders.find(o => o.id === id);
        if (item) {
          newSelectedItem = { ...item };
          newOrderId = item.id;
        }
      }

      setSelectedOrders(newSelected);
      setActiveOrderId(newOrderId);
      setActiveOrder(newSelectedItem);
      setSelectedItem(newSelectedItem);
    },
    [selectedOrders, orders, setSelectedOrders, setActiveOrderId, setActiveOrder]
  );

  const searchHandler = (value, tags = [], type = null) => {
    setSearchString(value);
    setSearchTags(tags);
    setSearchType(type);
    setFilterStatus('search');
    if (location.pathname !== '/orders') {
      navigate('/orders');
    }
  };

  const callActionHandler = useCallback(
    (paramsArg, action) => {
      const id = paramsArg?.orderId || params?.orderId || activeOrderId;
      switch (action) {
        case 'export':
          actionGeneratePhotos();
          break;
        case 'edit':
          orderShowEditAction();
          break;
        case 'delete':
          orderDeleteAction();
          break;
        case 'extra':
          navigate(`/orders/${id}/extra-actions`);
          break;
        case 'checklist-form':
          navigate(`/orders/${id}/checklist`);
          break;
        case 'repair-budget-form':
          navigate(`/orders/${id}/repair-budget`);
          break;
        case 'order-details':
          navigate(`/orders/${id}`);
          break;
        default:
          break;
      }
    },
    [activeOrderId, params?.orderId, navigate, actionGeneratePhotos, orderShowEditAction, orderDeleteAction]
  );

  const clearFilters = () => {
    setSearchString('');
    setSearchTags([]);
    setSearchType('');
  };

  const renderContent = () => {
    switch (component) {
      case 'orders-list':
        if (isLoading) return <Loading clientPanel />;
        if (orders.length === 0 && !isRefetching) {
          return (
            <Empty
              header={<FormattedMessage id="empty.listHeader" />}
              subHeader={<FormattedMessage id="empty.listSubHeader" />}
            />
          );
        }
        return (
          <OrdersList
            orderShowDetailsAction={orderShowDetailsAction}
            data={orders}
            checkOne={checkOneItemAction}
            selectedCheckboxes={selectedOrders}
            loadMoreItems={() => fetchNextPage()}
            hasMoreItems={hasNextPage}
            fromPage="client"
            isClientPanel
          />
        );
      case 'order-details':
        return (
          <OrderDetails
            changePage={callActionHandler}
            orderEdit={orderShowEditAction}
            currentPage="client"
            clientPanel
          />
        );
      case 'order-edit':
        return (
          <EditOrderFormPage
            editOrderId={params?.orderId || activeOrderId}
            successfulOrderEdition={id => navigate(`/orders/${id || params?.orderId || activeOrderId}`)}
            cancelEditOrder={() => navigate('/orders')}
            clientPanel
          />
        );
      case 'order-extra-actions':
        return (
          <ExtraActions
            changePage={callActionHandler}
            orderId={params?.orderId || activeOrderId}
            orderEdit={orderShowEditAction}
            clientPanel
          />
        );
      case 'checklist-form':
        return <ChecklistForm changePage={callActionHandler} orderId={params?.orderId || activeOrderId} clientPanel />;
      case 'repair-budget-form':
        return (
          <RepairBudgetForm changePage={callActionHandler} orderId={params?.orderId || activeOrderId} clientPanel />
        );
      default:
        return null;
    }
  };

  const requireToolbar = ['orders-list', 'order-details'].includes(component);
  const requireFiltersBar = component === 'orders-list';

  return (
    <div className="overflow-auto h-[calc(100vh-180px)] bg-white">
      {requireToolbar && (
        <OrderToolbar
          history={{
            push: navigate,
            replace: p => navigate(p, { replace: true }),
            goBack: () => navigate(-1),
            location,
          }}
          searchHandler={searchHandler}
          actionHandler={callActionHandler}
          selectedItem={selectedItem}
          selectedItems={selectedOrders}
          clearChosenTypeAndTags={!searchTags.length && !searchType}
          query={{ text: searchString, tags: searchTags, orderType: searchType }}
        />
      )}
      {requireFiltersBar && (
        <FeatureSwitch feature="ORDER_STATUS_FILTERS">
          <ClientFiltersBar
            activeStatus={filterStatus}
            orderByDir={filterOrderByDir}
            onStatusChange={handleStatusChange}
            onOrderByDirChange={handleOrderByDirChange}
          />
        </FeatureSwitch>
      )}
      <div
        className={clsx(
          'overflow-auto',
          requireFiltersBar ? 'h-[calc(100%-128px)]' : requireToolbar ? 'h-[calc(100%-65px)]' : 'h-full'
        )}
      >
        {renderContent()}
      </div>
    </div>
  );
};

ClientOrders.propTypes = {
  component: PropTypes.oneOf([
    'orders-list',
    'order-details',
    'order-edit',
    'order-extra-actions',
    'checklist-form',
    'repair-budget-form',
  ]).isRequired,
};

export default ClientOrders;
